import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Package, ShoppingCart, Users, FileText, Crown, GraduationCap, Heart, Share2, TrendingUp, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function TenantDashboard() {
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tenant/dashboard'),
      api.get('/tenant/dashboard/chart', { params: { days: 30 } }).catch(() => ({ data: { chart: [] } }))
    ]).then(([dashRes, chartRes]) => {
      setData(dashRes.data);
      setChartData(chartRes.data.chart || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const s = data?.stats || {};
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const stats = [
    { label: 'Produk', value: s.total_products, icon: Package, color: 'text-[#2D6CDF]', bg: 'bg-blue-50' },
    { label: 'Pesanan', value: s.total_orders, icon: ShoppingCart, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Pelanggan', value: s.total_customers, icon: Users, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Revenue', value: `Rp ${fmt(s.total_revenue)}`, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Invoice Unpaid', value: s.unpaid_invoices, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Member Aktif', value: s.active_members, icon: Crown, color: 'text-teal-600', bg: 'bg-teal-50' },
    { label: 'Kursus', value: s.total_courses, icon: GraduationCap, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Affiliate', value: s.total_affiliates, icon: Share2, color: 'text-pink-600', bg: 'bg-pink-50' },
  ];

  return (
    <div data-testid="tenant-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Dashboard</h1>
        {data?.tenant && <p className="text-sm text-slate-500 mt-1">Store: {data.tenant.subdomain}.tokomoe.com</p>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {stats.map(({ label, value, icon: Icon, color, bg }, i) => (
          <Card key={i} className="stat-card border-slate-100 shadow-sm" data-testid={`tenant-stat-${i}`}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={color} />
              </div>
              <p className="text-xl font-bold text-slate-900">{value ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue & Orders Charts */}
      {chartData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Revenue (30 Hari)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip formatter={(v) => [`Rp ${new Intl.NumberFormat('id-ID').format(v)}`, 'Revenue']} labelFormatter={l => `Tanggal: ${l}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#2D6CDF" fill="#2D6CDF" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Pesanan (30 Hari)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, 'Pesanan']} labelFormatter={l => `Tanggal: ${l}`} />
                  <Bar dataKey="orders" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Pesanan Terbaru</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-2 px-3 text-slate-500 font-medium">Order</th>
                <th className="text-left py-2 px-3 text-slate-500 font-medium">Customer</th>
                <th className="text-left py-2 px-3 text-slate-500 font-medium">Total</th>
                <th className="text-left py-2 px-3 text-slate-500 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {(data?.recent_orders || []).map((o, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2 px-3 font-mono text-xs">{o.order_number}</td>
                    <td className="py-2 px-3">{o.customer_name}</td>
                    <td className="py-2 px-3">Rp {fmt(o.total)}</td>
                    <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span></td>
                  </tr>
                ))}
                {!data?.recent_orders?.length && <tr><td colSpan={4} className="py-8 text-center text-slate-400">Belum ada pesanan</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
